// The player's own in-game notebook: a free-text "assumption" per rune (their guess
// at what it means -- the game never shows the real answer, per the "discovery over
// explanation" pillar in design/01_Vision.md) plus a running log of observations from
// spells actually cast. Persisted so a player's notes survive a reload.

export interface NotebookState {
  assumptions: Record<string, string>;
  observations: Record<string, string[]>;
}

const STORAGE_KEY = 'rune-world-game-notebook';

function load(): NotebookState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as NotebookState;
  } catch {
    // Corrupt/unreadable storage -- fall through to a blank notebook rather than
    // throwing during boot.
  }
  return { assumptions: {}, observations: {} };
}

const state: NotebookState = load();

function save(): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function getAssumption(runeId: string): string {
  return state.assumptions[runeId] ?? '';
}

export function setAssumption(runeId: string, text: string): void {
  state.assumptions[runeId] = text;
  save();
}

export function getObservations(runeId: string): string[] {
  return state.observations[runeId] ?? [];
}

export function addObservation(runeId: string, entry: string): void {
  if (!state.observations[runeId]) state.observations[runeId] = [];
  state.observations[runeId].push(entry);
  save();
}

export function resetNotebook(): void {
  state.assumptions = {};
  state.observations = {};
  save();
}

// Whole-state snapshot/restore for gameSave.ts.
export function getNotebookState(): NotebookState {
  return {
    assumptions: { ...state.assumptions },
    observations: Object.fromEntries(Object.entries(state.observations).map(([id, log]) => [id, [...log]])),
  };
}

export function setNotebookState(newState: NotebookState): void {
  state.assumptions = { ...newState.assumptions };
  state.observations = Object.fromEntries(Object.entries(newState.observations).map(([id, log]) => [id, [...log]]));
  save();
}
